/**
 * Nährwerte eines Rezepts für die gewählte Menge: je Portion, Person oder
 * Stück, bei Formen für die ganze Form. Eigene Werte aus den Produkten haben
 * Vorrang vor den Richtwerten; ein Hinweis sagt, wie viele Zutaten erfasst
 * sind.
 */
import {
  NUTRIENT_FIELDS,
  divideNutrients,
  formatDecimal,
  recipeNutrition,
  shoppingKey,
} from '@cookbook/domain';
import type { NutritionSource } from '@cookbook/domain';
import { useMemo } from 'react';
import type { BaseUnit, Ingredient, Product } from '../api/types.ts';
import { useCachedQuery } from '../api/useCachedQuery.ts';
import { texts } from '../texts/de.ts';

/** Bezeichnung der Bezugsgröße. */
const PER_LABELS: Readonly<Record<BaseUnit, string>> = {
  portions: texts.nutrition.perPortion,
  persons: texts.nutrition.perPerson,
  pieces: texts.nutrition.perPiece,
  pan: texts.nutrition.perPan,
};

/** Eigene Werte der Produkte, über Kennung und Namen auffindbar. */
function productSources(products: readonly Product[]) {
  const byId = new Map<string, NutritionSource>();
  const byKey = new Map<string, NutritionSource>();
  for (const product of products) {
    if (product.nutrition === null && product.pieceGrams === null) {
      continue;
    }
    const source: NutritionSource = { values: product.nutrition, pieceGrams: product.pieceGrams };
    byId.set(product.id, source);
    for (const name of [product.name, ...product.synonyms]) {
      byKey.set(shoppingKey(name), source);
    }
  }
  return { byId, byKey };
}

interface NutritionPanelProps {
  readonly ingredients: readonly Ingredient[];
  readonly baseUnit: BaseUnit;
  /** Umrechnungsfaktor der Mengen. */
  readonly factor: number;
  /** Gewählte Zahl der Portionen, Personen oder Stücke. */
  readonly amount: number;
}

/** Aufklappbare Nährwertangaben. */
export function NutritionPanel({ ingredients, baseUnit, factor, amount }: NutritionPanelProps) {
  const products = useCachedQuery<{ products: Product[] }>('/api/products', 'einkauf.produkte');
  const sources = useMemo(() => productSources(products.data?.products ?? []), [products.data]);
  const result = useMemo(
    () =>
      recipeNutrition(
        ingredients,
        factor,
        (ingredient) =>
          (ingredient.productId === null ? undefined : sources.byId.get(ingredient.productId)) ??
          sources.byKey.get(shoppingKey(ingredient.name)) ??
          null,
      ),
    [ingredients, factor, sources],
  );
  if (result.measurable === 0) {
    return null;
  }
  const values = divideNutrients(result.total, baseUnit === 'pan' ? 1 : amount);

  return (
    <details className="group rounded-card border border-border-subtle bg-surface-raised p-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2">
        <span className="font-semibold">{texts.nutrition.title}</span>
        <span className="text-sm text-content-muted">
          {result.counted === 0
            ? texts.nutrition.none
            : `${texts.nutrition.estimate}: ${formatDecimal(values.kcal, 0)} kcal ${PER_LABELS[baseUnit]}`}
          <span
            className="ml-2 inline-block transition-transform group-open:rotate-180 print:hidden"
            aria-hidden="true"
          >
            ▾
          </span>
        </span>
      </summary>
      {result.counted === 0 ? null : (
        <table className="mt-3 w-full text-sm">
          <tbody>
            {NUTRIENT_FIELDS.map((field) => (
              <tr key={field.key} className="border-t border-border-subtle">
                <th
                  scope="row"
                  className={`py-1.5 text-left font-normal ${field.key === 'sugar' ? 'pl-4 text-content-muted' : ''}`}
                >
                  {texts.nutrition.fields[field.key]}
                </th>
                <td className="py-1.5 text-right tabular-nums">
                  {formatDecimal(values[field.key], field.key === 'kcal' ? 0 : 1)} {field.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-3 text-xs text-content-muted">
        {texts.nutrition.coverage
          .replace('{counted}', String(result.counted))
          .replace('{measurable}', String(result.measurable))}
        {result.missing.length === 0
          ? null
          : ` ${texts.nutrition.missing.replace('{names}', result.missing.join(', '))}`}
      </p>
      <p className="mt-1 text-xs text-content-muted">{texts.nutrition.disclaimer}</p>
    </details>
  );
}
